/**
 * Converts a sentence to title case format.
 *
 * This function takes a string and capitalizes the first letter of each word,
 * while converting the rest of the letters to lowercase. Words are separated by spaces.
 *
 * @param sentence - The input string to convert to title case
 * @returns The title case formatted string, or empty string if input is invalid
 *
 * @example
 * ```typescript
 * toTitleCase('hello world') // Returns: 'Hello World'
 * toTitleCase('UPPERCASE TEXT') // Returns: 'Uppercase Text'
 * toTitleCase('mixed Case Words') // Returns: 'Mixed Case Words'
 * toTitleCase('') // Returns: ''
 * toTitleCase('   ') // Returns: ''
 * ```
 */
export const toTitleCase = (sentence: string) => {
    if (typeof sentence !== 'string' || sentence.trim().length === 0) {
        return ''
    }

    const lowercasedSentence = sentence.toLowerCase()

    const words = lowercasedSentence.split(' ')

    const titleCaseWords = words.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1)
    })

    return titleCaseWords.join(' ')
}

/**
 * Safely converts a string, number, or undefined value to a number.
 *
 * This function attempts to convert various input types to a number.
 * It handles edge cases like zero values and returns null for invalid inputs.
 *
 * @param value - The value to convert (can be string, number, or undefined)
 * @returns The converted number, or null if the value cannot be parsed
 *
 * @example
 * ```typescript
 * stringToNumber('100') // Returns: 100
 * stringToNumber(200) // Returns: 200
 * stringToNumber('0') // Returns: 0
 * stringToNumber(0) // Returns: 0
 * stringToNumber('invalid') // Returns: null
 * stringToNumber(undefined) // Returns: null
 * stringToNumber('') // Returns: null
 * ```
 */
export const stringToNumber = (
    value: string | number | undefined
): number | null => {
    if (!value && value !== 0) return null
    const parsed = Number(value)
    return isNaN(parsed) ? null : parsed
}

/**
 * Escapes special regex characters in a string to make it safe for use in RegExp.
 *
 * This function escapes characters that have special meaning in regular expressions,
 * preventing regex syntax errors when the string is used in pattern matching.
 * This is particularly useful for search/highlight functionality where user input
 * may contain characters like parentheses, brackets, or other regex metacharacters.
 *
 * @param text - The string to escape
 * @returns The escaped string safe for use in RegExp
 *
 * @example
 * ```typescript
 * escapeRegexCharacters('hello (world)') // Returns: 'hello \\(world\\)'
 * escapeRegexCharacters('test [123]') // Returns: 'test \\[123\\]'
 * escapeRegexCharacters('$100.00') // Returns: '\\$100\\.00'
 * escapeRegexCharacters('normal text') // Returns: 'normal text'
 * ```
 */
export const escapeRegexCharacters = (text: string): string => {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Escapes regex-operator characters in search text for backend database queries.
 *
 * Tilde (~) and pipe (|) are regex operators in PostgreSQL and some other
 * databases. Escaping them ensures the backend treats them as literal characters
 * rather than pattern matching operators. The escaping is done internally —
 * users still see the original characters in the search box.
 *
 * @param text - The search text to escape
 * @returns The escaped string safe for database queries
 *
 * @example
 * ```typescript
 * escapeSearchText('test~value') // Returns: 'test\\~value'
 * escapeSearchText('a|b')        // Returns: 'a\\|b'
 * escapeSearchText('normal text') // Returns: 'normal text'
 * ```
 */
export const escapeSearchText = (text: string): string =>
    text.replace(/[~|]/g, '\\$&')

/**
 * Inverse of {@link escapeSearchText}. Strips the backslash that was added
 * before regex-operator characters when the search text round-trips back
 * from the backend, so the search box doesn't display escape characters
 * the user never typed.
 *
 * Kept symmetric with `escapeSearchText`: extend both functions together
 * when escaping additional characters.
 */
export const unescapeSearchText = (text: string): string =>
    text.replace(/\\([~|])/g, '$1')

/**
 * Normalize U+00A0 non-breaking spaces to regular spaces for comparison.
 * Firefox's CKEditor and DOMParser convert &nbsp; entities to U+00A0 characters,
 * which causes false positive change detection since U+00A0 !== regular space.
 *
 * [Firefox 120+, CKEditor 5.x as of 2026-02]
 * TODO: Re-evaluate if Firefox/CKEditor behavior changes in future versions.
 */
export const normalizeSpaces = (text: string): string =>
    text.replace(/\u00A0/g, ' ')

/**
 * Normalize a search query to a backend/highlighter-friendly form. Behavior
 * depends on whether dashes are acting as token separators or letter joiners:
 *
 * - Dashes are treated as letter joiners (parts concatenated into one word)
 *   when EITHER:
 *     - any part is a single character (e.g. 'g-y-psum' → 'gypsum',
 *       'a-b-c' → 'abc'), OR
 *     - all parts are short, length ≤ 3 (e.g. 'wal-ls' → 'walls',
 *       'wal-l' → 'wall').
 *   This treats short hyphenated typos as a single word and prevents
 *   dropping stray single-letter tokens onto every letter in the document.
 * - Otherwise (at least one part is 4+ chars and no part is a single char),
 *   dashes are separators and replaced with spaces so the displayed format
 *   "061600 - Sheathing" tokenizes the same as "061600 Sheathing", and
 *   real compound queries like 'wall-in' / 'wall-ins' tokenize as
 *   'wall in' / 'wall ins' (matching 'Wall-Insulation').
 *
 * Whitespace adjacent to dashes is collapsed first so 'g - y - psum' behaves
 * the same as 'g-y-psum'.
 *
 * Regex-operator characters (tilde, pipe) are padded with whitespace so they
 * tokenize as standalone terms (consistent with how other special chars like
 * '@' behave). Without this, 'gypsum~' was being matched as 'gypsum' by the
 * backend (which strips the operator during tokenization) instead of
 * returning no results.
 */
export const normalizeSearchText = (text: string): string => {
    const collapsed = text.replace(/\s*-+\s*/g, '-').trim()
    if (collapsed === '') return ''

    const dashParts = collapsed.split(/-+/)
    const dashesAreJoiners =
        dashParts.length > 1 &&
        (dashParts.some((p) => p.length === 1) ||
            dashParts.every((p) => p.length <= 3))
    const dashReplacement = dashesAreJoiners ? '' : ' '

    return collapsed
        .replace(/-+/g, dashReplacement)
        .replace(/[~|]/g, ' $& ')
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * Build searchWords patterns for react-highlight-words so a multi-token query
 * highlights as one contiguous span across:
 *   - direct concatenation         e.g. 'pane ls' \u2192 'Panels'
 *   - whitespace/dash separators   e.g. '061600 sheathing' \u2192 '061600 - SHEATHING'
 *   - a token that matched as a prefix/suffix of a longer word, then a separator,
 *     then the next token         e.g. 'wal ins' \u2192 'Wall-Insulation'
 *   - tokens that appear in the document in REVERSED order from the query
 *                                  e.g. 'finishes 09' \u2192 '09 - FINISHES'
 * and still highlights tokens individually when no joint span exists.
 *
 * The joint bridge `\w*[\s-]*\w*` allows optional word characters around the
 * separator (so token1 can complete its word and token2 can start mid-word),
 * but does not span across an additional whitespace run \u2014 this preserves the
 * "tokens must be in the same phrase" guarantee (e.g. '061600 abc sheathing'
 * still does not match the joint pattern).
 *
 * For multi-token queries, both the typed-order joint and its reversed-order
 * counterpart are emitted so the highlighter can match either direction. The
 * highlighter library coalesces overlapping matches, so only the broader span
 * of the two is rendered when both would have hit the same region.
 *
 * Must be used with `<Highlighter autoEscape={false} ... />` since the joint
 * pattern is a regex.
 *
 * @example
 * buildSearchHighlightPatterns('061600 sheathing')
 *   // \u2192 [
 *   //   '061600\\w*[\\s-]*\\w*sheathing',
 *   //   'sheathing\\w*[\\s-]*\\w*061600',
 *   //   '061600',
 *   //   'sheathing',
 *   // ]
 *   // matches "061600 - SHEATHING" as one continuous highlight via the
 *   // forward joint and "FINISHES - 061600" via the reverse joint.
 */
export const buildSearchHighlightPatterns = (
    searchText: string | undefined
): string[] => {
    if (!searchText) return []
    const tokens = searchText
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(escapeRegexCharacters)
    if (tokens.length <= 1) return tokens
    const bridge = '\\w*[\\s-]*\\w*'
    return [
        tokens.join(bridge),
        [...tokens].reverse().join(bridge),
        ...tokens,
    ]
}
